import { Button as KitButton } from '@d-edge/overnight-hotelier-react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import styles from './ui.module.css'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'

const variation = { primary: 'solid', secondary: 'outline', ghost: 'ghost', danger: 'solid' } as const

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: 'sm' | 'md'
  loading?: boolean
  active?: boolean
  icon?: ReactNode
}

function classes(variant: Variant, size: 'sm' | 'md', active?: boolean, className?: string) {
  return [size === 'sm' ? styles.sm : '', variant === 'danger' ? styles.danger : '', active ? styles.active : '', className ?? ''].filter(Boolean).join(' ')
}

export function Button({ variant = 'secondary', size = 'md', loading, active, icon, className, children, type = 'button', ...rest }: ButtonProps) {
  return (
    <KitButton type={type} variation={variation[variant]} loading={loading} loadingLabel="Loading" aria-pressed={active} className={classes(variant, size, active, className)} {...rest}>
      {icon}
      {children}
    </KitButton>
  )
}

interface LinkButtonProps {
  href: string
  children: ReactNode
  variant?: Variant
  size?: 'sm' | 'md'
  icon?: ReactNode
  title?: string
}

export function LinkButton({ href, children, variant = 'secondary', size = 'md', icon, title }: LinkButtonProps) {
  return (
    <KitButton variation={variation[variant]} className={classes(variant, size)} asChild>
      <a href={href} target="_blank" rel="noreferrer" title={title}>
        {icon}
        {children}
      </a>
    </KitButton>
  )
}

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string
  children: ReactNode
}

/** Icon-only ghost button; `label` is the accessible name and tooltip. */
export function IconButton({ label, children, className, type = 'button', ...rest }: IconButtonProps) {
  return (
    <KitButton type={type} variation="ghost" className={`${styles.iconButton} ${className ?? ''}`} aria-label={label} title={label} {...rest}>
      {children}
    </KitButton>
  )
}
